import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { portalAppTitle, portalBasePath, resolveActivePortalKind, type PortalKind } from '../lib/portal';
import { getActivePortalKind, setActivePortalKind, type DbPortalKind } from '../lib/supabase';

type PortalContextValue = {
  kind: PortalKind;
  basePath: string;
  title: string;
};

const PortalContext = createContext<PortalContextValue | null>(null);

function dbKindForPortal(kind: PortalKind): DbPortalKind {
  if (kind === 'it') return 'it';
  if (kind === 'dem') return 'dem';
  if (kind === 'forms') return 'forms';
  return 'crm';
}

export function PortalProvider({ kind, children }: { kind: PortalKind; children: ReactNode }) {
  const dbKind = dbKindForPortal(kind);
  // Sync before paint/effects so soft-entered pages never fetch with a stale schema client.
  if (getActivePortalKind() !== dbKind) {
    setActivePortalKind(dbKind);
  }

  useEffect(() => {
    setActivePortalKind(dbKind);
    document.title = portalAppTitle(kind);
  }, [kind, dbKind]);

  const value: PortalContextValue = {
    kind,
    basePath: portalBasePath(kind),
    title: portalAppTitle(kind),
  };

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal(): PortalContextValue {
  const ctx = useContext(PortalContext);
  if (ctx) return ctx;
  const kind = resolveActivePortalKind();
  return {
    kind,
    basePath: portalBasePath(kind),
    title: portalAppTitle(kind),
  };
}

export function usePortalPath(subpath = ''): string {
  const { basePath } = usePortal();
  const clean = subpath.replace(/^\//, '');
  if (!clean) return basePath || '/';
  return basePath ? `${basePath}/${clean}` : `/${clean}`;
}
